// aiStagingSession — Pure-TS state-machine för AI-output som läggs i en
// temp-layer med cost-badge. Mirror av tourStagingSession, men istället
// för förseedade demo-blocks injicierar vi AI-genererad WorkflowState
// och taggar den med cost + correlation_id.
//
// Lifecycle:
//   beginAiStage(currentState, aiProposal, costCredits, correlationId)
//       → { stagedState, session }  (aiProposal visas i collapsed layer
//                                    märkt isAiProposal=true + kostnad)
//   approveAiStage(session)         → { newState, correlationId }
//                                      (merges AI-blocks into real flow,
//                                       caller commit:ar reservation)
//   rejectAiStage(session)          → { state: baseState }
//                                      (caller släpper reservation)
//
// Samma robust-mot-crash-pattern som tourStagingSession:
//   cleanupStaleAiLayers(state) säkerställer att övergivna AI-proposals
//   från föregående session försvinner vid nästa mount.

import type { WorkflowState, WorkflowBlock, BlockGroup, Connector } from '@/types/workflow';

export interface AiProposal {
  /** Blocks som AI föreslår. IDs ersätts så de inte krockar med existerande. */
  blocks: Omit<WorkflowBlock, 'id'>[];
  /** Connectors refererar via block-index. */
  connectors: Array<{
    fromBlockIdx: number;
    toBlockIdx: number;
    fromPort: Connector['fromPort'];
    toPort: Connector['toPort'];
    label?: string;
  }>;
  /** Valfri nested layer inuti proposal:et (t.ex. för större arkitekturer). */
  nestedGroups?: Array<{
    name: string;
    color: string;
    collapsed?: boolean;
    blockIndices: number[];
  }>;
  /** Human-readable label för cost-badgen. */
  proposalName: string;
}

export interface AiStagingSession {
  baseState: WorkflowState;
  tempLayerId: string;
  nestedLayerIds: string[];
  correlationId: string;
  costCredits: number;
  proposalName: string;
  startedAt: number;
}

export interface BeginResult {
  state: WorkflowState;
  session: AiStagingSession;
}

export interface ApproveResult {
  state: WorkflowState;
  correlationId: string;
  actualCost: number;
}

export interface RejectResult {
  state: WorkflowState;
  correlationId: string;
}

export type IdFn = () => string;

const defaultIdFn: IdFn = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `ai-${Math.random().toString(36).slice(2, 11)}`;

const AI_LAYER_COLOR = '#a855f7'; // violett — visuellt distinkt från tour (primär/accent)

export function beginAiStage(
  state: WorkflowState,
  proposal: AiProposal,
  costCredits: number,
  correlationId: string,
  idFn: IdFn = defaultIdFn,
  now: number = Date.now(),
): BeginResult {
  const newBlockIds = proposal.blocks.map(() => idFn());
  const newBlocks: WorkflowBlock[] = proposal.blocks.map((b, i) => ({
    ...b,
    id: newBlockIds[i],
    mtime: now,
  }));

  const newConnectors: Connector[] = proposal.connectors.map(c => ({
    id: idFn(),
    fromId: newBlockIds[c.fromBlockIdx],
    toId: newBlockIds[c.toBlockIdx],
    fromPort: c.fromPort,
    toPort: c.toPort,
    label: c.label,
  }));

  const nestedLayerIds: string[] = [];
  const nestedGroups: BlockGroup[] = (proposal.nestedGroups ?? []).map(ng => {
    const id = idFn();
    nestedLayerIds.push(id);
    return {
      id,
      name: ng.name,
      color: ng.color,
      blockIds: ng.blockIndices.map(i => newBlockIds[i]),
      collapsed: ng.collapsed ?? false,
      isAiProposal: true,
      aiCorrelationId: correlationId,
      mtime: now,
    } as BlockGroup;
  });

  const blockIdsInNested = new Set(nestedGroups.flatMap(g => g.blockIds));
  const outerBlockIds = newBlockIds.filter(id => !blockIdsInNested.has(id));

  const tempLayerId = idFn();
  const tempLayer: BlockGroup = {
    id: tempLayerId,
    name: `🤖 ${proposal.proposalName} (${costCredits} credits)`,
    color: AI_LAYER_COLOR,
    blockIds: outerBlockIds,
    collapsed: true,
    collapsedX: 0,
    collapsedY: 0,
    isAiProposal: true,
    aiCorrelationId: correlationId,
    aiCostCredits: costCredits,
    mtime: now,
  } as BlockGroup;

  const stagedState: WorkflowState = {
    ...state,
    blocks: [...state.blocks, ...newBlocks],
    connectors: [...state.connectors, ...newConnectors],
    groups: [...state.groups, tempLayer, ...nestedGroups],
  };

  const session: AiStagingSession = {
    baseState: state,
    tempLayerId,
    nestedLayerIds,
    correlationId,
    costCredits,
    proposalName: proposal.proposalName,
    startedAt: now,
  };

  return { state: stagedState, session };
}

/**
 * Approve = behåll AI-output, men "bake" in det i user's riktiga workflow
 * (flippar isAiProposal → false så layers inte städas bort nästa mount).
 * Caller måste sen commit:a reservation via `commit_credit_reservation` RPC.
 */
export function approveAiStage(
  state: WorkflowState,
  session: AiStagingSession,
): ApproveResult {
  const aiLayerIds = new Set([session.tempLayerId, ...session.nestedLayerIds]);
  const groups = state.groups.map(g =>
    aiLayerIds.has(g.id)
      ? {
          ...g,
          isAiProposal: false,
          aiCorrelationId: undefined,
          aiCostCredits: undefined,
          // rensa cost-prefix från namn
          name: g.name?.replace(/^🤖\s*/, '').replace(/\s*\(\d+\s+credits\)$/, ''),
        }
      : g,
  );
  return {
    state: { ...state, groups },
    correlationId: session.correlationId,
    actualCost: session.costCredits,
  };
}

/** Reject = riva AI-output, återställ baseState. Caller släpper reservation. */
export function rejectAiStage(session: AiStagingSession): RejectResult {
  return {
    state: session.baseState,
    correlationId: session.correlationId,
  };
}

/**
 * Städa övergivna AI-proposals från tidigare session. Körs vid mount.
 * Identiskt-nog till tourStagingSession.cleanupStaleTourLayers.
 */
export function cleanupStaleAiLayers(state: WorkflowState): WorkflowState {
  const aiGroupIds = new Set(
    state.groups.filter(g => (g as BlockGroup & { isAiProposal?: boolean }).isAiProposal).map(g => g.id),
  );
  if (aiGroupIds.size === 0) return state;

  const aiBlockIds = new Set<string>();
  for (const g of state.groups) {
    if (aiGroupIds.has(g.id)) {
      for (const bId of g.blockIds) aiBlockIds.add(bId);
    }
  }

  return {
    ...state,
    blocks: state.blocks.filter(b => !aiBlockIds.has(b.id)),
    connectors: state.connectors.filter(
      c => !aiBlockIds.has(c.fromId) && !aiBlockIds.has(c.toId),
    ),
    groups: state.groups.filter(g => !aiGroupIds.has(g.id)),
  };
}

export function countAiProposals(state: WorkflowState): number {
  return state.groups.filter(g => (g as BlockGroup & { isAiProposal?: boolean }).isAiProposal && !g.parentGroupId).length;
}

export function findAiProposal(
  state: WorkflowState,
  correlationId: string,
): BlockGroup | null {
  return state.groups.find(
    g =>
      (g as BlockGroup & { isAiProposal?: boolean; aiCorrelationId?: string }).isAiProposal &&
      (g as BlockGroup & { aiCorrelationId?: string }).aiCorrelationId === correlationId &&
      !g.parentGroupId,
  ) ?? null;
}

/**
 * beginLoadStage — variant av beginAiStage utan credit-tracking.
 * Används när user laddar en SPARAD artifact-version (Batch DDD/EEE) eller
 * en historisk AI-output till canvas. Ingen AI-call sker, ingen kostnad
 * debiteras — bara visuell preview som user kan Apply eller Discard.
 */
export function beginLoadStage(
  state: WorkflowState,
  proposal: AiProposal,
  correlationId: string,
  idFn: IdFn = defaultIdFn,
  now: number = Date.now(),
): BeginResult {
  // Identisk struktur som beginAiStage men kostnad = 0 och annorlunda label-prefix.
  const newBlockIds = proposal.blocks.map(() => idFn());
  const newBlocks: WorkflowBlock[] = proposal.blocks.map((b, i) => ({
    ...b, id: newBlockIds[i], mtime: now,
  }));

  const newConnectors: Connector[] = proposal.connectors.map(c => ({
    id: idFn(),
    fromId: newBlockIds[c.fromBlockIdx],
    toId: newBlockIds[c.toBlockIdx],
    fromPort: c.fromPort,
    toPort: c.toPort,
    label: c.label,
  }));

  const nestedLayerIds: string[] = [];
  const nestedGroups: BlockGroup[] = (proposal.nestedGroups ?? []).map(ng => {
    const id = idFn();
    nestedLayerIds.push(id);
    return {
      id, name: ng.name, color: ng.color,
      blockIds: ng.blockIndices.map(i => newBlockIds[i]),
      collapsed: ng.collapsed ?? false,
      isAiProposal: true,
      aiCorrelationId: correlationId,
      mtime: now,
    } as BlockGroup;
  });

  const blockIdsInNested = new Set(nestedGroups.flatMap(g => g.blockIds));
  const outerBlockIds = newBlockIds.filter(id => !blockIdsInNested.has(id));

  const tempLayerId = idFn();
  const tempLayer: BlockGroup = {
    id: tempLayerId,
    name: `📂 ${proposal.proposalName}`,
    color: '#a855f7',
    blockIds: outerBlockIds,
    collapsed: true,
    collapsedX: 0,
    collapsedY: 0,
    isAiProposal: true,
    aiCorrelationId: correlationId,
    aiCostCredits: 0, // ingen kostnad för load
    mtime: now,
  } as BlockGroup;

  const stagedState: WorkflowState = {
    ...state,
    blocks: [...state.blocks, ...newBlocks],
    connectors: [...state.connectors, ...newConnectors],
    groups: [...state.groups, tempLayer, ...nestedGroups],
  };

  const session: AiStagingSession = {
    baseState: state,
    tempLayerId,
    nestedLayerIds,
    correlationId,
    costCredits: 0,
    proposalName: proposal.proposalName,
    startedAt: now,
  };

  return { state: stagedState, session };
}

/** Konvertera fri WorkflowState från AI till AiProposal-format. */
export function proposalFromWorkflowState(
  ws: WorkflowState,
  proposalName: string = 'AI proposal',
): AiProposal {
  const blockIdToIdx = new Map<string, number>(ws.blocks.map((b, i) => [b.id, i]));
  return {
    blocks: ws.blocks.map(({ id: _id, ...rest }) => rest),
    connectors: ws.connectors
      .filter(c => blockIdToIdx.has(c.fromId) && blockIdToIdx.has(c.toId))
      .map(c => ({
        fromBlockIdx: blockIdToIdx.get(c.fromId)!,
        toBlockIdx: blockIdToIdx.get(c.toId)!,
        fromPort: c.fromPort,
        toPort: c.toPort,
        label: c.label,
      })),
    nestedGroups: ws.groups
      .filter(g => !g.parentGroupId)
      .map(g => ({
        name: g.name ?? 'Group',
        color: g.color,
        collapsed: g.collapsed,
        blockIndices: g.blockIds
          .map(id => blockIdToIdx.get(id))
          .filter((i): i is number => i !== undefined),
      })),
    proposalName,
  };
}
